import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ProcessedError, ErrorType, ErrorContext } from '../interfaces/error.interface';
import { AuthService } from '@auth/services/auth.service';

/**
 * Servicio centralizado para manejo de errores
 * Proporciona métodos para procesar, clasificar y transformar errores en mensajes user-friendly
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  private readonly authService = inject(AuthService);
  private lastAlertTime = 0;
  private readonly ALERT_COOLDOWN_MS = 5000;

  /**
   * Muestra una alerta de conexión de red al usuario con un cooldown para evitar duplicados
   */
  showNetworkAlert(): void {
    const now = Date.now();
    if (now - this.lastAlertTime > this.ALERT_COOLDOWN_MS) {
      this.lastAlertTime = now;
      alert('No se pudo establecer conexión con el servidor. Por favor, verifique su conexión a internet o el estado de la red');
    }
  }

  /**
   * Prefijos técnicos comunes del backend que deben removerse
   */
  private readonly defaultTechnicalPrefixes = [
    'Error al calcular cotización:',
    'Error al calcular primas usando QuoteFactory',
    'Error al calcular primas usando',
    'QuoteFactory',
    'dds_axd_QuoteIntelligent2',
    'Error al calcular primas:',
    'Error al procesar:',
    'System.Exception:',
    'System.InvalidOperationException:'
  ];

  /**
   * Patrones para identificar errores críticos que requieren contacto con ejecutivo
   */
  private readonly defaultCriticalPatterns = [
    'Condición Especial',
    'Ejecutivo Comercial',
    'comunicarse',
    'especial',
    'evaluarlo',
    'contacto con ejecutivo'
  ];

  /**
   * Patrones para identificar errores de validación que el usuario puede corregir
   */
  private readonly defaultValidationPatterns = [
    'Plan seleccionado',
    'no es validad',
    'no es válido',
    'no válido',
    'no válida',
    'edad del vehículo',
    'años de edad',
    'vehículo seleccionado',
    // Rango asegurable de mercado (Andromeda). El backend devuelve el mensaje ya redactado:
    // "TOYOTA COROLLA 2020 ES ASEGURABLE ENTRE $970,000.00 y $1,310,000.00".
    // Es un error que el usuario corrige cambiando el valor, no un fallo del sistema.
    'asegurable',
    'no puede ser',
    'debe ser',
    'requerido',
    'obligatorio'
  ];

  /**
   * Mensajes genéricos por tipo de error HTTP
   */
  private readonly httpErrorMessages: Record<number, string> = {
    0: 'No se pudo establecer conexión con el servidor. Por favor, verifique su conexión a internet o el estado de la red.',
    400: 'La solicitud no es válida. Por favor, verifique los datos ingresados.',
    401: 'No tiene autorización para realizar esta acción.',
    403: 'No tiene permisos para realizar esta acción.',
    404: 'El recurso solicitado no fue encontrado.',
    409: 'Ya existe un registro con estos datos.',
    422: 'Los datos proporcionados no son válidos.',
    500: 'Error interno del servidor. Por favor, intente nuevamente más tarde.',
    502: 'Error de comunicación con el servidor. Por favor, intente nuevamente.',
    503: 'El servicio no está disponible en este momento. Por favor, intente más tarde.',
    504: 'Tiempo de espera agotado. Por favor, intente nuevamente.'
  };

  /**
   * Procesa un error HTTP y lo transforma en un ProcessedError
   * @param error - Error HTTP o cualquier error
   * @param context - Contexto adicional para el procesamiento
   * @returns Error procesado y listo para mostrar
   */
  processError(error: unknown, context?: ErrorContext): ProcessedError {
    // Si es un HttpErrorResponse, procesarlo específicamente
    if (error instanceof HttpErrorResponse) {
      return this.processHttpError(error, context);
    }

    // Si es un error genérico
    if (error instanceof Error) {
      return this.processGenericError(error, context);
    }

    // Error desconocido
    return this.processUnknownError(error, context);
  }

  /**
   * Procesa un error HTTP específicamente
   */
  private processHttpError(error: HttpErrorResponse, context?: ErrorContext): ProcessedError {
    const statusCode = error.status !== undefined && error.status !== null ? error.status : 500;
    const rawMessage = this.extractErrorMessage(error);
    const cleanMessage = this.cleanTechnicalMessage(rawMessage, context?.technicalPrefixes);

    // Clasificar el tipo de error
    const errorType = this.classifyError(cleanMessage, statusCode, context);

    // Determinar si el usuario puede reintentar
    const canRetry = this.canRetry(statusCode, errorType);

    // Obtener mensaje final
    const userMessage = this.buildUserMessage(cleanMessage, errorType, statusCode, context);

    // Obtener acciones sugeridas
    const suggestedActions = this.getSuggestedActions(errorType, cleanMessage);

    // Logging estructurado (en desarrollo, podría enviarse a un servicio de logging)
    this.logError(error, context, errorType);

    // Si el error es 401, cerrar sesión automáticamente
    if (statusCode === 401) {
      this.authService.logout();
    }

    return {
      message: userMessage,
      type: errorType,
      statusCode,
      canRetry,
      suggestedActions,
      originalError: error
    };
  }

  /**
   * Procesa un error genérico (Error object)
   */
  private processGenericError(error: Error, context?: ErrorContext): ProcessedError {
    const cleanMessage = this.cleanTechnicalMessage(error.message, context?.technicalPrefixes);
    const errorType = this.classifyError(cleanMessage, undefined, context);

    this.logError(error, context, errorType);

    return {
      message: cleanMessage || 'Ocurrió un error inesperado.',
      type: errorType,
      canRetry: errorType !== 'critical',
      originalError: error
    };
  }

  /**
   * Procesa un error desconocido
   */
  private processUnknownError(error: unknown, context?: ErrorContext): ProcessedError {
    const errorString = String(error);
    const cleanMessage = this.cleanTechnicalMessage(errorString, context?.technicalPrefixes);
    const errorType = this.classifyError(cleanMessage, undefined, context);

    this.logError(error, context, errorType);

    return {
      message: cleanMessage || 'Ocurrió un error desconocido.',
      type: 'unknown',
      canRetry: true,
      originalError: error
    };
  }

  /**
   * Extrae el mensaje de error de un HttpErrorResponse
   */
  private extractErrorMessage(error: HttpErrorResponse): string {
    // Intentar obtener el mensaje de diferentes lugares
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.message) {
      return error.message;
    }
    if (typeof error.error === 'string') {
      return error.error;
    }
    return 'Error desconocido';
  }

  /**
   * Limpia el mensaje de error removiendo prefijos técnicos
   */
  private cleanTechnicalMessage(message: string, customPrefixes?: string[]): string {
    if (!message) return '';

    let cleanMessage = message.trim();

    // Evitar exponer mensajes técnicos internos de Angular que contienen endpoints y detalles HTTP
    if (cleanMessage.includes('Http failure response for')) {
      return '';
    }

    const prefixes = customPrefixes || this.defaultTechnicalPrefixes;

    // 1. Remover prefijos técnicos conocidos de la lista
    prefixes.forEach(prefix => {
      if (cleanMessage.includes(prefix)) {
        const prefixIndex = cleanMessage.indexOf(prefix);
        if (prefixIndex !== -1) {
          const nextColonIndex = cleanMessage.indexOf(':', prefixIndex + prefix.length);
          if (nextColonIndex !== -1) {
            cleanMessage = cleanMessage.substring(nextColonIndex + 1).trim();
          } else {
            cleanMessage = cleanMessage.substring(prefixIndex + prefix.length).trim();
            if (cleanMessage.startsWith(':')) {
              cleanMessage = cleanMessage.substring(1).trim();
            }
          }
        }
      }
    });

    // 2. Si todavía hay algún prefijo técnico genérico antes de un ":" (ej. que contenga dds_, exception, error, etc.), removerlo
    let hasTechnicalPrefix = true;
    while (hasTechnicalPrefix) {
      const firstColonIndex = cleanMessage.indexOf(':');
      if (firstColonIndex !== -1 && firstColonIndex < cleanMessage.length - 1) {
        const beforeColon = cleanMessage.substring(0, firstColonIndex).toLowerCase();
        // Si la parte antes del primer ":" es técnica, la removemos
        if (beforeColon.includes('error') ||
            beforeColon.includes('exception') ||
            beforeColon.includes('factory') ||
            beforeColon.includes('dds_') ||
            beforeColon.includes('system.') ||
            beforeColon.includes('quotefactory') ||
            beforeColon.trim().length === 0) {
          cleanMessage = cleanMessage.substring(firstColonIndex + 1).trim();
        } else {
          hasTechnicalPrefix = false;
        }
      } else {
        hasTechnicalPrefix = false;
      }
    }

    return cleanMessage || message; // Si después de limpiar está vacío, devolver el original
  }

  /**
   * Clasifica el error según su tipo
   */
  private classifyError(message: string, statusCode?: number, context?: ErrorContext): ErrorType {
    const lowerMessage = message.toLowerCase();
    const criticalPatterns = context?.criticalPatterns || this.defaultCriticalPatterns;
    const validationPatterns = context?.validationPatterns || this.defaultValidationPatterns;

    // Verificar si es un error crítico
    if (criticalPatterns.some(pattern => lowerMessage.includes(pattern.toLowerCase()))) {
      return 'critical';
    }

    // Verificar si es un error de validación
    if (validationPatterns.some(pattern => lowerMessage.includes(pattern.toLowerCase())) ||
        statusCode === 400 || statusCode === 422) {
      return 'validation';
    }

    // Clasificar por código HTTP
    if (statusCode !== undefined && statusCode !== null) {
      if (statusCode >= 500) {
        return 'server';
      }
      if (statusCode === 408 || statusCode === 504 || statusCode === 0) {
        return 'network';
      }
    }

    return 'unknown';
  }

  /**
   * Determina si el usuario puede reintentar la operación
   */
  private canRetry(statusCode?: number, errorType?: ErrorType): boolean {
    if (errorType === 'critical') {
      return false; // Errores críticos no se pueden reintentar
    }
    if (errorType === 'validation') {
      return false; // Errores de validación requieren corrección, no reintento
    }
    if (statusCode === 401 || statusCode === 403) {
      return false; // Errores de autorización no se pueden reintentar
    }
    if (statusCode !== undefined && statusCode !== null && (statusCode >= 500 || statusCode === 408 || statusCode === 504 || statusCode === 0)) {
      return true; // Errores de servidor o red pueden reintentarse
    }
    return true;
  }

  /**
   * Construye el mensaje final para el usuario
   */
  private buildUserMessage(
    cleanMessage: string,
    errorType: ErrorType,
    statusCode?: number,
    context?: ErrorContext
  ): string {
    // Si es un error crítico, mostrar el mensaje con emoji
    if (errorType === 'critical') {
      if (cleanMessage && cleanMessage.trim().length > 0) {
        return `⚠️ ${cleanMessage}`;
      }
    }

    // Si es un error de validación, mostrar el mensaje con emoji
    if (errorType === 'validation') {
      if (cleanMessage && cleanMessage.trim().length > 0) {
        return `⚠️ ${cleanMessage}`;
      }
    }

    // Si es un error 401 (autorización), manejarlo dinámicamente según la inactividad (presencia de token)
    if (statusCode === 401) {
      const hasToken = typeof localStorage !== 'undefined' && localStorage.getItem('token');
      if (hasToken) {
        return 'Su sesión ha expirado por inactividad. Por favor, recargue la página o inicie sesión nuevamente.';
      } else {
        return 'No tiene autorización para realizar esta acción. Por favor, inicie sesión con una cuenta válida.';
      }
    }

    // Si hay un mensaje genérico para el código HTTP, usarlo
    if (statusCode !== undefined && this.httpErrorMessages[statusCode] !== undefined) {
      if (errorType === 'network' || errorType === 'server') {
        // Para errores de red y servidor, no queremos mostrar detalles técnicos o URLs al usuario
        return this.httpErrorMessages[statusCode];
      }
      if (cleanMessage && cleanMessage.length > 0) {
        return `${this.httpErrorMessages[statusCode]} ${cleanMessage}`;
      }
      return this.httpErrorMessages[statusCode];
    }

    // Si el mensaje limpio está vacío, usar mensaje genérico
    if (!cleanMessage || cleanMessage.length === 0) {
      return statusCode && this.httpErrorMessages[statusCode]
        ? this.httpErrorMessages[statusCode]
        : 'Ocurrió un error inesperado. Por favor, intente nuevamente.';
    }

    // Devolver el mensaje limpio
    return cleanMessage;
  }

  /**
   * Obtiene acciones sugeridas según el tipo de error
   */
  private getSuggestedActions(errorType: ErrorType, message: string): string[] {
    const actions: string[] = [];

    if (errorType === 'validation') {
      if (message.toLowerCase().includes('plan')) {
        actions.push('Vaya al paso de Coberturas y seleccione un plan válido');
      }
      if (message.toLowerCase().includes('vehículo') || message.toLowerCase().includes('edad')) {
        actions.push('Vaya al paso de Vehículo y verifique los datos');
      }
    }

    if (errorType === 'critical') {
      actions.push('Contacte a su ejecutivo comercial para más información');
    }

    if (errorType === 'network' || errorType === 'server') {
      actions.push('Verifique su conexión a internet');
      actions.push('Intente nuevamente en unos momentos');
    }

    return actions;
  }

  /**
   * Registra el error de forma estructurada
   * En producción, esto podría enviarse a un servicio de logging
   */
  private logError(error: unknown, context?: ErrorContext, errorType?: ErrorType): void {
    // En desarrollo, solo loguear. En producción, esto podría enviarse a un servicio externo
    if (typeof window !== 'undefined' && window.__ENV__?.production !== true) {
      const logData = {
        timestamp: new Date().toISOString(),
        operation: context?.operation,
        errorType,
        error: error instanceof HttpErrorResponse
          ? {
              status: error.status,
              statusText: error.statusText,
              url: error.url,
              message: error.message
            }
          : error,
        metadata: context?.metadata
      };

      // Solo loguear en desarrollo (no en producción)
      if (errorType === 'critical' || errorType === 'server') {
        // Loguear errores críticos y de servidor más prominentemente
      }
    }
  }

  /**
   * Método de conveniencia para procesar errores de cotización
   * Incluye contexto específico para operaciones de cotización
   */
  processQuoteError(error: unknown, operation: 'create' | 'calculate' | 'get' | 'list' = 'create'): ProcessedError {
    const context: ErrorContext = {
      operation: `quote${operation.charAt(0).toUpperCase() + operation.slice(1)}`,
      technicalPrefixes: this.defaultTechnicalPrefixes,
      criticalPatterns: this.defaultCriticalPatterns,
      validationPatterns: this.defaultValidationPatterns
    };

    return this.processError(error, context);
  }

  /**
   * Método de conveniencia para procesar errores de autenticación
   */
  processAuthError(error: unknown): ProcessedError {
    const context: ErrorContext = {
      operation: 'authentication',
      technicalPrefixes: [],
      criticalPatterns: [],
      validationPatterns: []
    };

    const processed = this.processError(error, context);

    // Mensajes específicos para autenticación
    if (processed.statusCode === 401) {
      processed.message = 'Usuario o contraseña incorrectos';
      processed.type = 'validation';
      processed.canRetry = false;
    }

    return processed;
  }

  /**
   * Método de conveniencia para procesar errores de catálogos
   */
  processCatalogError(error: unknown): ProcessedError {
    const context: ErrorContext = {
      operation: 'loadCatalog',
      technicalPrefixes: this.defaultTechnicalPrefixes,
      criticalPatterns: [],
      validationPatterns: []
    };

    const processed = this.processError(error, context);

    // Mensaje específico para errores de catálogo
    if (!processed.message || processed.message.includes('Error desconocido')) {
      processed.message = 'Error al cargar los catálogos. Por favor, recargue la página.';
    }

    return processed;
  }
}
